// src/App.js
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Building2, QrCode, ArrowUpDown } from "lucide-react";
import ElevatorControl from './components/ElevatorControl';
import QRAccessControl from './components/QRAccessControl';




const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Načtení stavu přihlášení z localStorage při inicializaci
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  const [loginData, setLoginData] = useState(() => {
    // Načtení přihlašovacích údajů z localStorage
    const savedData = localStorage.getItem('loginData');
    return savedData ? JSON.parse(savedData) : { 
      username: '', 
      password: '',
      remember: false
    };
  });

  const [loginError, setLoginError] = useState('');

  // Uložení stavu přihlášení do localStorage při změně
  useEffect(() => {
    localStorage.setItem('isLoggedIn', isLoggedIn);
  }, [isLoggedIn]);

  // Uložení přihlašovacích údajů do localStorage při změně
  useEffect(() => {
    if (loginData.remember) {
      localStorage.setItem('loginData', JSON.stringify(loginData));
    } else {
      localStorage.removeItem('loginData');
    }
  }, [loginData]);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError('');

    if (loginData.username === 'admin' && loginData.password === 'Zelenatrava1+') {
      setIsLoggedIn(true);
    } else {
      setLoginError('Nesprávné přihlašovací údaje');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    if (!loginData.remember) {
      setLoginData({ username: '', password: '', remember: false });
    }
  };

  // Přidáme state pro výtah
  const [elevatorStatus, setElevatorStatus] = useState({
    currentFloor: 1,
    doorStatus: 'closed',
    isOperational: true,
    inProgress: false
  });


  // Funkce pro volání výtahu
  const callElevator = async (floor) => {
    // Zabránit volání pokud již probíhá jiná akce
    if (elevatorStatus.inProgress) {
      return;
    }

    setElevatorStatus(prev => ({ ...prev, inProgress: true }));

    try {
      // Zde bude volání na backend API
      // Například:
      // const response = await fetch('/api/elevator/call', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({ targetFloor: floor })
      // });

      // Pro demonstraci simulujeme volání výtahu
      console.log(`Calling elevator to floor ${floor}`);
      
      // Simulace pohybu výtahu
      setTimeout(() => {
        setElevatorStatus(prev => ({
          ...prev,
          currentFloor: floor,
          inProgress: false
        }));
      }, 2000);

    } catch (error) {
      console.error('Failed to call elevator:', error);
      setElevatorStatus(prev => ({ ...prev, inProgress: false }));
    }
  };

  // Komponenta pro tlačítko patra
  const FloorButton = ({ floor }) => {
    const isCurrentFloor = elevatorStatus.currentFloor === floor;
    const isDisabled = elevatorStatus.inProgress || isCurrentFloor;

    return (
      <Button
        key={floor}
        variant={isCurrentFloor ? "secondary" : "outline"}
        disabled={isDisabled}
        onClick={() => callElevator(floor)}
        className={`relative ${isCurrentFloor ? 'bg-blue-100' : ''}`}
      >
        <span>Floor {floor}</span>
        {elevatorStatus.inProgress && elevatorStatus.currentFloor !== floor && (
          <span className="absolute right-2 w-2 h-2 rounded-full animate-pulse bg-blue-500" />
        )}
      </Button>
    );
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              QR ENTRY System
            </CardTitle>
            <CardDescription>
              Please log in to access the QR ENTRY
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder="Username"
                  value={loginData.username}
                  onChange={(e) => setLoginData(prev => ({
                    ...prev, 
                    username: e.target.value
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="password"
                  placeholder="Password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({
                    ...prev, 
                    password: e.target.value
                  }))}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="remember"
                  checked={loginData.remember}
                  onChange={(e) => setLoginData(prev => ({
                    ...prev,
                    remember: e.target.checked
                  }))}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="remember" className="text-sm text-gray-600">
                  Zapamatovat přihlašovací údaje
                </label>
              </div>
              {loginError && (
                <div className="text-red-500 text-sm p-2 bg-red-50 rounded">
                  {loginError}
                </div>
              )}
              <Button type="submit" className="w-full">
                Log In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto space-y-4">
        <header className="flex justify-between items-center">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-6 w-6" />
            QR ENTRY System
          </h1>
          <Button variant="outline" onClick={handleLogout}>
            Log Out
          </Button>
        </header>

        <Tabs defaultValue="qr" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="qr" className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              QR Access
            </TabsTrigger>
            <TabsTrigger value="elevator" className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4" />
              Elevator Control
            </TabsTrigger>
          </TabsList>

          <TabsContent value="qr">
            <QRAccessControl />
          </TabsContent>

          <TabsContent value="elevator">
            <Card>
              <CardHeader>
                <CardTitle>Elevator Control & Monitoring</CardTitle>
                <CardDescription>
                  Monitor and control elevator operations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ElevatorControl />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default App;